import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { profileUpdateSchema, ProfileUpdateFormData } from "../schemas";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ProfilePhotoUpload } from "./ProfilePhotoUpload";
import { updateProfile, StudentProfile } from "@/services/student/profile.service";
import { useAppToast } from "@/hooks/useAppToast";

interface ProfileFormProps {
  profile?: StudentProfile;
  onSuccess?: () => void;
}

export const ProfileForm: React.FC<ProfileFormProps> = ({ profile, onSuccess }) => {
  const toast = useAppToast();
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm<ProfileUpdateFormData>({
    resolver: zodResolver(profileUpdateSchema),
    mode: "onChange",
    defaultValues: {
      name: profile?.name || "",
      phone: profile?.phone || "",
      street: profile?.street || "",
      number: profile?.number || "",
      zipCode: profile?.zipCode || "",
      neighborhood: profile?.neighborhood || "",
      city: profile?.city || "",
      state: profile?.state || "",
      country: profile?.country || "",
      photoFilename: profile?.photoFilename || null,
    },
  });

  // Reset form when profile changes (e.g., after refetch)
  useEffect(() => {
    if (profile) {
      reset({
        name: profile.name || "",
        phone: profile.phone || "",
        street: profile.street || "",
        number: profile.number || "",
        zipCode: profile.zipCode || "",
        neighborhood: profile.neighborhood || "",
        city: profile.city || "",
        state: profile.state || "",
        country: profile.country || "",
        photoFilename: profile.photoFilename || null,
      });
    }
  }, [profile, reset]);

  const photoFilename = watch("photoFilename");

  const onSubmit = async (data: ProfileUpdateFormData) => {
    try {
      // Include email from profile (required by backend but not editable)
      await updateProfile({
        ...data,
        email: profile?.email || '',
      });
      toast.success("Perfil atualizado com sucesso!");
      onSuccess?.();
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast.error(error?.response?.data?.message || "Erro ao atualizar perfil");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dados do Perfil</CardTitle>
        <CardDescription>
          Atualize suas informações pessoais e endereço
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Foto de Perfil */}
          <div className="space-y-2">
            <Label>Foto de Perfil</Label>
            <ProfilePhotoUpload
              value={photoFilename}
              onChange={(value) => setValue("photoFilename", value || null)}
              disabled={isSubmitting}
            />
          </div>

          {/* Nome */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Nome <span className="text-destructive">*</span>
            </Label>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <Input
                  {...field}
                  id="name"
                  placeholder="Seu nome completo"
                  disabled={isSubmitting}
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? "name-error" : undefined}
                />
              )}
            />
            {errors.name && (
              <span id="name-error" className="text-sm text-destructive" role="alert">
                {errors.name.message}
              </span>
            )}
          </div>

          {/* Telefone */}
          <div className="space-y-2">
            <Label htmlFor="phone">Telefone</Label>
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <Input
                  {...field}
                  id="phone"
                  type="tel"
                  placeholder="(11) 99999-9999"
                  disabled={isSubmitting}
                  aria-invalid={!!errors.phone}
                  aria-describedby={errors.phone ? "phone-error" : undefined}
                />
              )}
            />
            {errors.phone && (
              <span id="phone-error" className="text-sm text-destructive" role="alert">
                {errors.phone.message}
              </span>
            )}
          </div>

          {/* Endereço */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Endereço</h3>

            {/* Rua */}
            <div className="space-y-2">
              <Label htmlFor="street">Rua</Label>
              <Controller
                control={control}
                name="street"
                render={({ field }) => (
                  <Input
                    {...field}
                    id="street"
                    placeholder="Nome da rua"
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>

            {/* Número e CEP */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="number">Número</Label>
                <Controller
                  control={control}
                  name="number"
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="number"
                      placeholder="123"
                      disabled={isSubmitting}
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zipCode">CEP</Label>
                <Controller
                  control={control}
                  name="zipCode"
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="zipCode"
                      placeholder="00000-000"
                      disabled={isSubmitting}
                      aria-invalid={!!errors.zipCode}
                      aria-describedby={errors.zipCode ? "zipCode-error" : undefined}
                    />
                  )}
                />
                {errors.zipCode && (
                  <span id="zipCode-error" className="text-sm text-destructive" role="alert">
                    {errors.zipCode.message}
                  </span>
                )}
              </div>
            </div>

            {/* Bairro */}
            <div className="space-y-2">
              <Label htmlFor="neighborhood">Bairro</Label>
              <Controller
                control={control}
                name="neighborhood"
                render={({ field }) => (
                  <Input
                    {...field}
                    id="neighborhood"
                    placeholder="Nome do bairro"
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>

            {/* Cidade, Estado e País */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">Cidade</Label>
                <Controller
                  control={control}
                  name="city"
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="city"
                      placeholder="Cidade"
                      disabled={isSubmitting}
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">Estado</Label>
                <Controller
                  control={control}
                  name="state"
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="state"
                      placeholder="UF"
                      maxLength={2}
                      disabled={isSubmitting}
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">País</Label>
                <Controller
                  control={control}
                  name="country"
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="country"
                      placeholder="País"
                      disabled={isSubmitting}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* Botão de Submit */}
          <div className="flex justify-end">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
